// src/cqcColors.js — shared colour helpers, same green->red language as
// RegulatoryCompliance.js's pctBg/pctBgStrong, plus CQC's own 4-band rating
// scale (Outstanding / Good / Requires improvement / Inadequate).

import React from "react";

export function pctBg(pct) {
  if (pct == null || Number.isNaN(pct)) return "#eee";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 75%, 85%)`;
}

export function pctBgStrong(pct) {
  if (pct == null || Number.isNaN(pct)) return "#ddd";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 70%, 72%)`;
}

export const fmtPct = (n) => (n == null || Number.isNaN(n) ? "—" : `${Math.round(n)}%`);

const RATING_META = {
  "Outstanding":            { bg: "#F6FFED", fg: "#389E0D", border: "#B7EB8F" },
  "Good":                   { bg: "#E6F7FF", fg: "#0958D9", border: "#91D5FF" },
  "Requires improvement":   { bg: "#FFFBE6", fg: "#AD6800", border: "#FFE58F" },
  "Inadequate":             { bg: "#FFF1F0", fg: "#A8071A", border: "#FFA39E" },
};
export function ratingMeta(rating) {
  return RATING_META[rating] || { bg: "#F5F5F5", fg: "#5D5D5D", border: "#F0F0F0" };
}

export function RatingBadge({ rating }) {
  const m = ratingMeta(rating);
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11,
      fontWeight: 600, background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
      whiteSpace: "nowrap",
    }}>
      {rating || "Not yet rated"}
    </span>
  );
}

const REQ_META = {
  mandatory:   { label: "Mandatory",   bg: "#FFF1F0", fg: "#A8071A", border: "#FFA39E" },
  recommended: { label: "Recommended", bg: "#FFF7E6", fg: "#AD4E00", border: "#FFD666" },
  optional:    { label: "Optional",    bg: "#F0F5FF", fg: "#1D39C4", border: "#ADC6FF" },
};
export function ReqBadge({ requirement }) {
  const m = REQ_META[requirement] || REQ_META.optional;
  return (
    <span style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 999, fontSize: 10,
      fontWeight: 600, background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
    }}>
      {m.label}
    </span>
  );
}

// Worklist status colours — see cqcRollup.js's elementStatus() for how a
// status is derived (UPDATE 7: manually set via a dropdown, not inferred).
// Red is "not-started", amber is "ongoing", green is "completed", grey
// ("not-applicable") is used sparingly since the Worklist filters those rows
// out by default. "overdue"/"in-progress" are kept defined (same colours as
// their nearest replacement) purely so nothing throws if older stored data
// or another view still passes one in — elementStatus() itself no longer
// produces them.
const STATUS_META = {
  "not-started":    { label: "Not started", dot: "#CF1322", bg: "#FFF1F0", fg: "#A8071A", border: "#FFA39E" },
  "overdue":        { label: "Overdue",     dot: "#CF1322", bg: "#FFF1F0", fg: "#A8071A", border: "#FFA39E" },
  "ongoing":        { label: "In progress", dot: "#d97706", bg: "#FFF7E6", fg: "#AD4E00", border: "#FFD666" },
  "in-progress":    { label: "In progress", dot: "#d97706", bg: "#FFF7E6", fg: "#AD4E00", border: "#FFD666" },
  "completed":      { label: "Completed",   dot: "#389E0D", bg: "#F6FFED", fg: "#389E0D", border: "#B7EB8F" },
  "not-applicable": { label: "N/A",         dot: "#8C8C8C", bg: "#F5F5F5", fg: "#5D5D5D", border: "#F0F0F0" },
};
export function statusMeta(status) {
  return STATUS_META[status] || STATUS_META["not-started"];
}
export function StatusBadge({ status }) {
  const m = statusMeta(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
      whiteSpace: "nowrap",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

// Pale full-row tint + a bold left edge in the same hue, so a Worklist row's
// background itself communicates status at a glance (UPDATE 7: "the status
// changes the row's background colour") without fighting the badge/text
// contrast — these are deliberately much lighter than STATUS_META's badge
// colours, which stay saturated since they sit on white.
const ROW_TINT = {
  "not-started":    { background: "#FFF1F0", borderLeft: "4px solid #F5222D" },
  "ongoing":        { background: "#FFFBE6", borderLeft: "4px solid #FA8C16" },
  "completed":      { background: "#F6FFED", borderLeft: "4px solid #52C41A" },
  "not-applicable": { background: "#FAFAFA", borderLeft: "4px solid #D9D9D9" },
};
export function statusRowStyle(status) {
  return ROW_TINT[status] || ROW_TINT["not-started"];
}
